import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import TermsAcceptanceModal from '../components/TermsAcceptanceModal';
import CompanyModal from '../components/CompanyModal';
import NonIndexableHelmet from '../components/NonIndexableHelmet';
import { getTokenMeta } from '../data/nonIndexableMeta';
import { useLocale } from '../contexts/LocaleContext';
import { localizePath } from '../utils/localePaths';

const MagicLinkPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login, user, refreshUser, updateUser } = useAuth();
  const { localePrefix } = useLocale();
  const { t } = useTranslation('auth');
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [userData, setUserData] = useState(null);
  const isVerifyingRef = useRef(false);

  const dashPath = (role) => localizePath(role === 'admin' ? '/admin' : '/dashboard', localePrefix);

  useEffect(() => {
    const verifyToken = async () => {
      if (isVerifyingRef.current) {
        return;
      }

      if (!token) {
        return;
      }

      isVerifyingRef.current = true;
      let verificationSuccessful = false;

      try {
        const response = await authAPI.verifyMagicLink(token);

        const authToken = response.data.token;
        const apiUserData = response.data.user;

        setUserData(apiUserData);

        if (!apiUserData.termsAcceptedAt) {
          login(authToken, apiUserData);
          setStatus('terms');
          setShowTermsModal(true);
          verificationSuccessful = true;
        } else if (apiUserData.needsCompany) {
          login(authToken, apiUserData);
          setStatus('company');
          setShowCompanyModal(true);
          verificationSuccessful = true;
        } else {
          login(authToken, apiUserData);
          setStatus('success');
          toast.success(t('magic.toastOk'));
          verificationSuccessful = true;

          setTimeout(() => {
            navigate(dashPath(apiUserData.role), { replace: true });
          }, 500);
        }
      } catch (err) {
        console.error('Error verifying magic link:', err);
        setStatus('error');
        setError(err.response?.data?.message || t('magicLink.invalidLink'));
      } finally {
        if (!verificationSuccessful) {
          isVerifyingRef.current = false;
        }
      }
    };

    verifyToken();
  }, [token, login, navigate, localePrefix]);

  const handleAcceptTerms = async () => {
    setAcceptingTerms(true);
    try {
      const response = await authAPI.acceptTerms();

      let updatedUser = response.data?.user;
      if (!updatedUser) {
        try {
          updatedUser = await refreshUser();
        } catch (refreshError) {
          if (refreshError.response?.status !== 429) {
            throw refreshError;
          }
          updatedUser = response.data?.user;
        }
      }

      setShowTermsModal(false);
      setStatus('success');
      toast.success(t('magicLink.termsAcceptedToast'));

      const finalUserData = updatedUser || userData || user;

      if (finalUserData?.needsCompany) {
        setStatus('company');
        setShowCompanyModal(true);
      } else {
        setTimeout(() => {
          navigate(dashPath(finalUserData?.role), { replace: true });
        }, 500);
      }
    } catch (err) {
      console.error('Error accepting terms:', err);
      if (err.response?.status !== 429) {
        toast.error(t('magicLink.termsErr'));
      }
      setAcceptingTerms(false);
    }
  };

  const handleDeclineTerms = () => {
    setShowTermsModal(false);
    setStatus('error');
    setError(t('magicLink.termsDecline'));
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const handleCompanyComplete = async (updatedUser) => {
    setShowCompanyModal(false);
    updateUser(updatedUser);
    setStatus('success');
    toast.success(t('magicLink.companyToast'));

    setTimeout(() => {
      navigate(dashPath(updatedUser?.role), { replace: true });
    }, 500);
  };

  const loginPath = localizePath('/login', localePrefix);

  return (
    <div className="auth-page-gradient">
      <NonIndexableHelmet {...getTokenMeta('/magic-link')} />
      <Container>
        <Row className="justify-content-center align-items-center auth-page-shell">
          <Col md={6} lg={5}>
            <Card className="shadow-lg border-0 rounded-lg">
              <Card.Body className="p-5 text-center">
                {status === 'verifying' && (
                  <>
                    <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                    <h3 className="mt-4 fw-bold">{t('magic.verifying')}</h3>
                    <p className="text-muted">{t('magicLink.verifyWait')}</p>
                  </>
                )}

                {status === 'terms' && (
                  <>
                    <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                    <h3 className="mt-4 fw-bold">{t('magicLink.termsPrep')}</h3>
                    <p className="text-muted">{t('magicLink.termsHint')}</p>
                  </>
                )}

                {status === 'company' && (
                  <>
                    <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                    <h3 className="mt-4 fw-bold">{t('magicLink.companyPrep')}</h3>
                    <p className="text-muted">{t('magicLink.companyHint')}</p>
                  </>
                )}

                {status === 'success' && (
                  <>
                    <div style={{ fontSize: '4rem', color: '#28a745' }}>
                      <i className="bi bi-check-circle-fill" />
                    </div>
                    <h3 className="mt-3 fw-bold text-success">{t('magicLink.successTitle')}</h3>
                    <Alert variant="success" className="mt-3 text-start">
                      <strong>{t('magicLink.welcomeTitle')}</strong>
                      <p className="mb-0 mt-2">{t('magicLink.welcomeBody')}</p>
                    </Alert>
                    <p className="text-muted">{t('magicLink.redirectDash')}</p>
                  </>
                )}

                {status === 'error' && (
                  <>
                    <div style={{ fontSize: '4rem', color: '#dc3545' }}>
                      <i className="bi bi-x-circle-fill" />
                    </div>
                    <h3 className="mt-3 fw-bold text-danger">{t('magicLink.errorTitle')}</h3>
                    <Alert variant="danger" className="mt-3 text-start">
                      {error}
                    </Alert>
                    <p className="text-muted mt-3">{t('magicLink.errorHint')}</p>
                    <div className="d-grid gap-2 mt-4">
                      <a href={loginPath} className="btn btn-primary btn-lg">
                        <i className="bi bi-arrow-left me-2" />
                        {t('magicLink.requestNew')}
                      </a>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <TermsAcceptanceModal
        show={showTermsModal}
        onAccept={handleAcceptTerms}
        onDecline={handleDeclineTerms}
        loading={acceptingTerms}
      />

      <CompanyModal show={showCompanyModal} onComplete={handleCompanyComplete} loading={false} />
    </div>
  );
};

export default MagicLinkPage;
